/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { TextInput } from 'src/components/ts-components/TextInput'
import HockeyCanadaLogo from 'src/pages/GeneralSettings/HockeyCanada/components/HockeyCanadaLogo'
import colors from '@sportninja/common/constants/appColors'
import { useRegistrationSetupContext } from '../../context'

/**
 * Integrations tab of the Registration Setup.
 *
 * Holds per-registration integration configuration. Currently the Hockey
 * Canada team ID, which is sent alongside validation requests for this
 * registration.
 */
const Integrations = () => {
  const { state, setState } = useRegistrationSetupContext()

  const isHockeyCanadaEnabled = Boolean(state.hockeyCanadaEnabled)

  /**
   * Toggling only flips the enabled flag — the team ID is deliberately kept so
   * turning the integration off and back on doesn't lose what was entered.
   */
  const handleToggle = (enabled: boolean) => {
    setState({ ...state, hockeyCanadaEnabled: enabled })
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;
      `}
    >
      <Text variant='captionLarge' weight='semiBold'>
        {capitalizeWords('Integrations')}
      </Text>

      {/* ---------- Hockey Canada ---------- */}
      <div css={SectionHeading}>
        <HockeyCanadaLogo height={28} />
        <Text variant='paragraphMedium' weight='semiBold'>
          Hockey Canada
        </Text>
      </div>

      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 24px;
          align-self: stretch;
        `}
      >
        <SwitchInput
          title='Enable Hockey Canada Integrations'
          subtitle='Validate Hockey Canada player numbers during registration and ensure compliance with Hockey Canada requirements.'
          value={isHockeyCanadaEnabled}
          onChange={handleToggle}
        />
      </div>

      {/* Only shown while the integration is on. The value itself is kept in
          state either way, so toggling off and back on restores it. */}
      {isHockeyCanadaEnabled ? (
        <div css={NestedUnderToggle}>
          <TextInput
            name='hockey_canada_team_id'
            label={capitalizeWords('Hockey Canada Team ID')}
            subtitle='Hockey Canada Team ID is used to reference a specific Season within an Organization ID'
            value={state.hockeyCanadaTeamId || ''}
            setValue={(value) =>
              setState({ ...state, hockeyCanadaTeamId: value })
            }
            placeholder='Enter Hockey Canada Team ID'
            fieldErrors={state.fieldErrors}
            customError={state.customError}
          />
        </div>
      ) : null}
    </div>
  )
}

export default Integrations

// ---- styled ----

/** Integration logo + name, shown at the top of each section. */
const SectionHeading = css`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`

/** Fields owned by the toggle above them, indented behind a left rule. */
const NestedUnderToggle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-self: stretch;
  width: 50%;
  /* margin indents the rule itself; padding is the gap from rule to field. */
  margin-left: 16px;
  padding-left: 32px;
  border-left: 2px solid ${colors.SOFT_STEEL};

  @media (max-width: 768px) {
    width: 100%;
  }
`
